// client/src/coms/organisation/DiscordImport.js
// Import from a Discord Server Template: paste a discord.new / discord.com/template link, preview the structure,
// choose what to import, confirm, see the result. Used by Server Settings → Import & Migration (into this server
// or as a new server) and by Create Server (new server only). The server reads the template from Discord's
// public API and does the import in one transaction (routes/comms/organisation/discordImport.js).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiPostJson } from './orgApi';

const LINK_RE = /^(?:https?:\/\/)?(?:www\.)?(?:discord\.new\/|(?:(?:canary|ptb)\.)?discord(?:app)?\.com\/templates?\/|discord\.gg\/template\/)[A-Za-z0-9-]{2,64}\/?(?:[?#].*)?$|^[A-Za-z0-9-]{8,64}$/i;

const OPTION_LABELS = [
  ['categories', 'Categories', 'Without them, the channels are added without a category.'],
  ['roles', 'Roles', 'Creates the template’s roles (never above your own). Needs Manage roles.'],
  ['permissions', 'Private channels & permissions', 'Keeps channels the template hides private, and imports channel permission overrides (overrides need Manage roles).'],
  ['topics', 'Channel topics', ''],
];

const ACTION_TEXT = { create: 'New', skip: 'Skipped', merge: 'Added to existing', reuse: 'Existing', flatten: 'No category' };

function Badge({ action, reason }) {
  return (
    <span className={`vxo-imp-badge is-${action}`} title={reason || ''}>
      {ACTION_TEXT[action] || action}
    </span>
  );
}

export function DiscordImportPanel({ serverKey = '', serverName = '', allowNewServer = true, allowExisting = true, onImported, onCancel }) {
  const [link, setLink] = useState('');
  const [dest, setDest] = useState(allowExisting && serverKey ? 'existing' : 'new');
  const [newName, setNewName] = useState('');
  const [include, setInclude] = useState({ categories: true, roles: true, permissions: true, topics: true });
  const [exclude, setExclude] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const seq = useRef(0);

  const linkOk = LINK_RE.test(link.trim());
  const path = (step) => (dest === 'existing' && serverKey ? `/servers/${encodeURIComponent(serverKey)}/discord-import/${step}` : `/discord-import/${step}`);

  async function loadPreview() {
    if (!linkOk) {
      setError('Paste a Discord server template link, such as https://discord.new/AbCdEf123456.');
      return;
    }
    const n = ++seq.current;
    setBusy(true);
    setError('');
    try {
      const r = await apiPostJson(path('preview'), { link: link.trim(), include, exclude });
      if (n !== seq.current) return;
      setPreview(r);
      if (!newName) setNewName(r.template.guild_name || r.template.name || '');
    } catch (e) {
      if (n === seq.current) {
        setPreview(null);
        setError((e && e.message) || 'Could not read this template.');
      }
    } finally {
      if (n === seq.current) setBusy(false);
    }
  }

  // The plan depends on the options and destination: refresh the preview when they change.
  const optionsKey = JSON.stringify([include, exclude, dest]);
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    if (preview) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsKey]);

  async function commit() {
    setBusy(true);
    setError('');
    try {
      const r = await apiPostJson(path('commit'), { link: link.trim(), include, exclude, confirm: true, name: dest === 'new' ? newName.trim() : undefined });
      setResult(r);
      setConfirming(false);
      try {
        window.dispatchEvent(new CustomEvent('vox-channels-changed'));
      } catch {}
      if (onImported) onImported(r);
    } catch (e) {
      setConfirming(false);
      setResult({ ok: false, error: (e && e.message) || 'The import failed. Nothing was changed.' });
    } finally {
      setBusy(false);
    }
  }

  const toggleChannel = (id) => setExclude((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));
  const counts = preview ? preview.counts : null;
  const nothing = counts && !counts.text && !counts.voice && !counts.categories && !counts.roles;

  const channelRow = (c) => (
    <li key={c.discord_id} className="vxo-imp-chan">
      <label className="vxo-check">
        <input type="checkbox" checked={c.action !== 'skip'} disabled={c.action === 'skip' && c.reason !== 'Not selected'} onChange={() => toggleChannel(c.discord_id)} aria-label={`Import ${c.name}`} />
        <span aria-hidden="true">{c.kind === 'voice' ? '🔊' : '#'}</span>
        <span className="vxo-imp-name">{c.name}</span>
        {c.private ? <span className="vxo-imp-badge is-private">Private</span> : null}
        {c.announcement ? <span className="vxo-imp-badge">Announcements</span> : null}
        <Badge action={c.action} reason={c.reason} />
      </label>
      {c.topic ? <div className="vxo-imp-sub">{c.topic}</div> : null}
      {c.action === 'skip' && c.reason ? <div className="vxo-imp-sub">{c.reason}</div> : null}
      {c.notes && c.notes.length ? <div className="vxo-imp-sub">{c.notes.join(' · ')}</div> : null}
    </li>
  );

  const summary = useMemo(() => {
    if (!result || !result.ok) return null;
    const s = result.summary || {};
    const parts = [];
    if (s.categories) parts.push(`${s.categories} categor${s.categories === 1 ? 'y' : 'ies'}`);
    if (s.text) parts.push(`${s.text} text channel${s.text === 1 ? '' : 's'}`);
    if (s.voice) parts.push(`${s.voice} voice channel${s.voice === 1 ? '' : 's'}`);
    if (s.roles) parts.push(`${s.roles} role${s.roles === 1 ? '' : 's'}`);
    if (s.overrides) parts.push(`${s.overrides} permission override${s.overrides === 1 ? '' : 's'}`);
    return parts.length ? parts.join(', ') : 'nothing new';
  }, [result]);

  if (result && result.ok) {
    const s = result.summary || {};
    return (
      <div className="vxo-imp" data-testid="discord-import-result">
        <div className="vxa-flash" role="status">
          Imported {summary}
          {result.mode === 'new' ? ' into a new server.' : '.'}
        </div>
        {s.categories_merged ? <p className="vxa-muted">{s.categories_merged} existing categor{s.categories_merged === 1 ? 'y was' : 'ies were'} reused.</p> : null}
        {s.skipped && s.skipped.length ? (
          <details>
            <summary>{s.skipped.length} skipped</summary>
            <ul className="vxo-plain">
              {s.skipped.map((x, i) => (
                <li key={i}>
                  {x.kind === 'voice' ? '🔊' : '#'}
                  {x.name} — {x.reason}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
        {result.unsupported && result.unsupported.length ? (
          <details>
            <summary>Not imported (Voxemera has no equivalent)</summary>
            <ul className="vxo-plain">
              {result.unsupported.map((u, i) => (
                <li key={i}>
                  <b>{u.what}</b>
                  {u.detail ? ` — ${u.detail}` : ''}
                  {u.count > 1 ? ` (${u.count})` : ''}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
        <div className="vxo-imp-foot">
          {result.mode === 'new' && result.server_key ? (
            <button type="button" className="vx-btn primary" onClick={() => window.dispatchEvent(new CustomEvent('vox-open-server', { detail: { serverKey: result.server_key } }))} data-testid="discord-import-open">
              Open the new server
            </button>
          ) : null}
          <button
            type="button"
            className="vx-btn"
            onClick={() => {
              setResult(null);
              setPreview(null);
              setLink('');
              setExclude([]);
            }}
          >
            Import another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="vxo-imp" data-testid="discord-import">
      <form
        className="vxo-inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          setExclude([]);
          loadPreview();
        }}
      >
        <input className="vxo-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://discord.new/…" aria-label="Discord template link" data-testid="discord-import-link" autoComplete="off" spellCheck={false} />
        <button type="submit" className="vx-btn primary" disabled={busy || !link.trim()} data-testid="discord-import-preview">
          {busy && !preview ? 'Reading…' : 'Preview'}
        </button>
      </form>
      <p className="vxa-muted vxa-small">
        In Discord: Server Settings → Server Template → copy the link. Only the structure is imported — never messages, members or files. No Discord sign-in or bot is needed.
      </p>
      {error ? (
        <div className="vxa-flash is-error" role="alert" data-testid="discord-import-error">
          {error}
        </div>
      ) : null}
      {result && !result.ok ? (
        <div className="vxa-flash is-error" role="alert" data-testid="discord-import-failed">
          {result.error} Nothing was changed.
        </div>
      ) : null}

      {preview ? (
        <div className="vxo-imp-preview" data-testid="discord-import-plan">
          <div className="vxo-imp-head">
            <h3 className="vxo-h3">{preview.template.name}</h3>
            <span className="vxa-muted vxa-small">
              {preview.template.creator ? `by ${preview.template.creator} · ` : ''}
              {preview.template.usage_count ? `used ${preview.template.usage_count.toLocaleString()} times` : ''}
            </span>
            {preview.template.description ? <p className="vxa-muted">{preview.template.description}</p> : null}
          </div>

          {allowNewServer && allowExisting && serverKey ? (
            <fieldset className="vxo-imp-dest">
              <legend>Import into</legend>
              <label className="vxo-check">
                <input type="radio" name="vx-imp-dest" checked={dest === 'existing'} onChange={() => setDest('existing')} />
                <span>This server{serverName ? ` (${serverName})` : ''} — adds channels, keeps everything already here</span>
              </label>
              <label className="vxo-check">
                <input type="radio" name="vx-imp-dest" checked={dest === 'new'} onChange={() => setDest('new')} />
                <span>A new server</span>
              </label>
            </fieldset>
          ) : null}
          {dest === 'new' ? (
            <label className="vxo-field">
              <span>New server name</span>
              <input value={newName} maxLength={80} onChange={(e) => setNewName(e.target.value)} data-testid="discord-import-name" />
            </label>
          ) : null}

          <fieldset className="vxo-imp-options">
            <legend>What to import</legend>
            {OPTION_LABELS.map(([k, label, hint]) => (
              <label key={k} className="vxo-check" title={hint}>
                <input type="checkbox" checked={!!include[k]} onChange={(e) => setInclude((x) => ({ ...x, [k]: e.target.checked }))} />
                <span>
                  {label}
                  {hint ? <span className="vxa-muted vxa-small"> — {hint}</span> : null}
                </span>
              </label>
            ))}
          </fieldset>

          <h4 className="vxo-h3">Channels</h4>
          <ul className="vxo-plain vxo-imp-tree">
            {preview.items.map((it) =>
              it.kind === 'category' ? (
                <li key={it.discord_id} className="vxo-imp-cat">
                  <div className="vxo-imp-catname">
                    ▾ {it.name} <Badge action={it.action} reason={it.reason} />
                  </div>
                  <ul className="vxo-plain">{it.children.map(channelRow)}</ul>
                </li>
              ) : (
                channelRow(it)
              )
            )}
          </ul>

          {preview.roles.length ? (
            <>
              <h4 className="vxo-h3">Roles</h4>
              <ul className="vxo-plain">
                {preview.roles.map((r) => (
                  <li key={r.discord_id}>
                    <span className="vxo-imp-dot" style={{ background: r.color || 'var(--vx-muted, #99a)' }} aria-hidden="true" /> {r.name} <Badge action={r.action} reason={r.reason} />
                    <span className="vxa-muted vxa-small">
                      {' '}
                      {r.admin ? 'Administrator — every permission' : `${r.keys.length} permission${r.keys.length === 1 ? '' : 's'}`}
                      {r.dropped && r.dropped.length ? ` · ${r.dropped.length} you cannot grant are left off` : ''}
                      {r.action !== 'create' && r.reason ? ` · ${r.reason}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {preview.unsupported.length ? (
            <details className="vxo-imp-unsupported" open>
              <summary>Not imported — Voxemera has no equivalent ({preview.unsupported.length})</summary>
              <ul className="vxo-plain">
                {preview.unsupported.map((u, i) => (
                  <li key={i}>
                    <b>{u.what}</b>
                    {u.detail ? ` — ${u.detail}` : ''}
                    {u.count > 1 ? ` (${u.count})` : ''}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          <div className="vxo-imp-foot">
            <span className="vxa-muted" data-testid="discord-import-counts">
              Will add: {counts.categories} categor{counts.categories === 1 ? 'y' : 'ies'}, {counts.text} text, {counts.voice} voice channel{counts.voice === 1 ? '' : 's'}
              {counts.roles ? `, ${counts.roles} role${counts.roles === 1 ? '' : 's'}` : ''}
              {counts.skipped ? ` · ${counts.skipped} skipped` : ''}
            </span>
            {onCancel ? (
              <button type="button" className="vx-btn" onClick={onCancel}>
                Cancel
              </button>
            ) : null}
            <button type="button" className="vx-btn primary" disabled={busy || nothing || (dest === 'new' && newName.trim().length < 2)} onClick={() => setConfirming(true)} data-testid="discord-import-start">
              {dest === 'new' ? 'Create server…' : 'Import…'}
            </button>
          </div>
        </div>
      ) : null}

      {confirming ? (
        <div className="vxo-backdrop vxo-imp-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && setConfirming(false)}>
          <div className="vxo-dialog" role="dialog" aria-modal="true" aria-label="Confirm import">
            <div className="vxo-dialog-head">
              <h2 className="vxo-dialog-title">{dest === 'new' ? `Create “${newName.trim()}”?` : 'Import into this server?'}</h2>
            </div>
            <div className="vxo-dialog-body">
              <p>
                This adds {counts.categories} categor{counts.categories === 1 ? 'y' : 'ies'}, {counts.text} text and {counts.voice} voice channel{counts.voice === 1 ? '' : 's'}
                {counts.roles ? ` and ${counts.roles} role${counts.roles === 1 ? '' : 's'}` : ''}
                {dest === 'existing' ? ' after what the server already has. Nothing existing is changed or removed.' : ' to a new server you own.'}
              </p>
              <p className="vxa-muted vxa-small">It happens in one step: if anything fails, nothing is imported.</p>
            </div>
            <div className="vxo-dialog-foot">
              <button type="button" className="vx-btn" disabled={busy} onClick={() => setConfirming(false)}>
                Cancel
              </button>
              <button type="button" className="vx-btn primary" disabled={busy} onClick={commit} data-testid="discord-import-confirm">
                {busy ? 'Importing…' : dest === 'new' ? 'Create server' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default DiscordImportPanel;
